import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import '../Styles/header.css';

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'about' | 'platforms' | null
  const headerRef = useRef(null);
  const hoverCloseTimer = useRef(null);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  // Desktop hover support: open immediately on enter, close on leave with
  // a short delay so moving the mouse from the toggle button down into the
  // dropdown menu (across the small gap) doesn't accidentally close it.
  // Only wired up for pointer devices that support hover (desktop/laptop);
  // touch/mobile continues to rely on the existing tap-to-toggle behavior.
  const openDropdownOnHover = (name) => {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
    setOpenDropdown(name);
  };

  const closeDropdownOnHoverLeave = () => {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    hoverCloseTimer.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  // Close the open dropdown whenever the user clicks/taps anywhere
  // outside the header (blank page area, another section, etc.),
  // or presses Escape.
  useEffect(() => {
    if (!openDropdown) return;

    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDropdown]);

  // Clean up any pending hover-close timer on unmount.
  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    };
  }, []);

  return (
    <header className="header" ref={headerRef}>
      <div className="header-logo">
        <Link to="/" onClick={closeMenu}>
          <img
            src="/assets/vnkm_logo.png"
            alt=""
            className="logo-img"
          />
        </Link>
      </div>

      <div className="header-right">
        <nav className={`header-nav ${menuOpen ? 'active' : ''}`}>
          <Link to="/home" className="nav-link" onClick={closeMenu}>Home</Link>

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('about')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('about')}
              aria-expanded={openDropdown === 'about'}
            >
              About us
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'about' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'about' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/whatwedo " className="dropdown-link" onClick={closeMenu}>
                  What We Do
                </Link>
                <Link to="/ourteam" className="dropdown-link" onClick={closeMenu}>
                  Our Team
                </Link>
                <Link to="/ourpartner" className="dropdown-link" onClick={closeMenu}>
                  Our Partners
                </Link>
              </div>
            )}
          </div>

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('platforms')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('platforms')}
              aria-expanded={openDropdown === 'platforms'}
            >
              Our Platforms
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'platforms' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'platforms' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/hrcommunity" className="dropdown-link" onClick={closeMenu}>
                  HR Community Network
                </Link>
                <Link to="/industryacademia" className="dropdown-link" onClick={closeMenu}>
                  Industry-Academia Connect
                </Link>
                <Link to="/employeebranding" className="dropdown-link" onClick={closeMenu}>
                  Employer Branding & Recognition
                </Link>
              </div>
            )}
          </div>


          <Link to="/get-certified" className="nav-link" onClick={closeMenu}>Get Certified</Link>
          <Link to="/events" className="nav-link" onClick={closeMenu}>Events</Link>
          <Link to="/gallery" className="nav-link" onClick={closeMenu}>Gallery</Link>

          <div className="header-cta mobile-cta">
            <Link to="/join" className="join-btn" onClick={closeMenu}>
              Join the Community
              <img
                src="/assets/home_arrow_icon.png"
                alt="Arrow Icon"
                className="arrow-icon"
              />
            </Link>
          </div>
        </nav>

        <div className="header-cta desktop-cta">
          <Link to="/jointhecommunity" className="join-btn">
            Join the Community
              <img
                src="/assets/home_arrow_icon.png"
                alt="Arrow Icon"
                className="arrow-icon"
              />
          </Link>
        </div>
      </div>

      <button
        className={`burger-btn ${menuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={menuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {menuOpen && <div className="nav-overlay" onClick={closeMenu}></div>}
    </header>
  );
}

export default Header;
